import { css } from '@emotion/react'
import { Alert, Button, HFlow, Text, VFlow } from 'bold-ui'
import { RequiredLabel } from 'components/form/core/RequiredLabel/RequiredLabel'
import theme from 'config/theme'
import { useForm } from 'react-final-form'
import { MetaPath } from 'util/metaPath'
import { createFormSectionStyles } from 'view/cidadao/utils-cidadao'

import { FormRowSimNao } from '../../../../components/form/field/FormRowSimNao'
import { CidadaoAldeadoFormModel, InformacoesSocioEconomicasFormModel } from '../types/model-cidadao'
import { CidadaoAldeadoBeneficiosFieldGroup } from './CidadaoAldeadoBeneficiosFieldGroup'
import { FormSectionAccordionWithErrors } from './FormSectionAccordionWithErrors'

interface InformacoesSocioEconomicasFormSectionProps {
  meta: MetaPath<InformacoesSocioEconomicasFormModel>
  values: InformacoesSocioEconomicasFormModel
  metaCidadaoAldeado: MetaPath<CidadaoAldeadoFormModel>
  cidadaoAldeado: CidadaoAldeadoFormModel
  isCidadaoAldeado?: boolean
}

export const InformacoesSocioEconomicasFormSection = ({
  meta,
  values,
  metaCidadaoAldeado,
  cidadaoAldeado,
  isCidadaoAldeado,
}: InformacoesSocioEconomicasFormSectionProps) => {
  const required =
    !!values?.alimentosAcabaramAntesTerDinheiroComprarMais || !!values?.comeuAlgunsAlimentosDinheiroAcabou

  const form = useForm()

  const handleClear = () => {
    form.batch(() => {
      form.change(meta.alimentosAcabaramAntesTerDinheiroComprarMais.absolutePath(), null)
      form.change(meta.comeuAlgunsAlimentosDinheiroAcabou.absolutePath(), null)
    })
  }

  const styles = {
    ...createFormSectionStyles(theme),
    labelContainer: css`
      background-color: ${theme.pallete.gray.c90};
      width: 100%;
      justify-content: flex-start;
      padding: 0.8rem 1rem;
    `,
    labelText: css`
      font-size: 14px;
      margin-left: 0;
    `,
  }

  return (
    <FormSectionAccordionWithErrors
      name={meta.openedAccordion}
      title='Informações socioeconômicas'
      uuid='informacoesSocioEconomicas'
      textStyle={css`
        font-size: 1rem;
      `}
      panelStyle={styles.rowsContainer}
    >
      {isCidadaoAldeado && (
        <VFlow vSpacing={0.1}>
          <CidadaoAldeadoBeneficiosFieldGroup
            name={metaCidadaoAldeado?.beneficios}
            values={cidadaoAldeado?.beneficios}
          />
        </VFlow>
      )}

      <div css={styles.labelContainer}>
        {required ? (
          <RequiredLabel
            fontWeight='bold'
            label='Triagem para Risco de Insegurança Alimentar (TRIA)'
            style={styles.labelText}
          />
        ) : (
          <Text style={styles.labelText} fontWeight='bold'>
            Triagem para Risco de Insegurança Alimentar (TRIA)
          </Text>
        )}
      </div>

      <HFlow>
        <Alert type='info' inline>
          Para registrar a <strong>TRIA</strong> todas as perguntas devem ser respondidas.
        </Alert>
        <Button size='small' kind='primary' skin='outline' onClick={handleClear}>
          Limpar campos
        </Button>
      </HFlow>

      <FormRowSimNao
        name={meta.alimentosAcabaramAntesTerDinheiroComprarMais}
        title='Nos últimos três meses, os alimentos acabaram antes que você tivesse dinheiro para comprar mais comida?'
        required={required}
      />

      <FormRowSimNao
        name={meta.comeuAlgunsAlimentosDinheiroAcabou}
        title='Nos últimos três meses, você comeu apenas alguns alimentos que ainda tinha, porque o dinheiro acabou?'
        required={required}
      />
    </FormSectionAccordionWithErrors>
  )
}
